/**
 * MandateUploadPage.js
 * =====================
 * Workflow ultra-simplifié de création de mandat :
 * 1. Upload du PDF (drag-drop) OU photo (caméra mobile)
 * 2. Loader IA pendant extraction (Gemini Vision)
 * 3. Formulaire pré-rempli, éditable + AI cross-check découvertes
 * 4. Optionnel : invitation email du vendeur
 */
/* eslint-disable react/no-unescaped-entities, no-empty */
import React, { useCallback, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../../contexts/AuthContext';
import api, { invalidateApiCache } from '../../lib/api';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import {
  Upload, FileText, Sparkles, Loader2, CheckCircle2, ArrowLeft,
  Plus, Trash2, Mail, User as UserIcon, Phone, Home, MapPin, Euro, Send,
  Camera, Image as ImageIcon, Link2,
} from 'lucide-react';

const ACCEPTED_EXTS = ['.pdf', '.jpg', '.jpeg', '.png', '.webp', '.heic', '.heif'];
const ACCEPTED_MIMES = 'application/pdf,image/jpeg,image/png,image/webp,image/heic,image/heif';

const PROPERTY_TYPES = [
  { value: 'apartment', label: 'Appartement' },
  { value: 'house', label: 'Maison' },
  { value: 'land', label: 'Terrain' },
  { value: 'commercial', label: 'Local commercial' },
  { value: 'building', label: 'Immeuble' },
];

const MANDATE_TYPES = [
  { value: 'simple', label: 'Simple' },
  { value: 'exclusif', label: 'Exclusif' },
  { value: 'semi_exclusif', label: 'Semi-exclusif' },
];

function StepDot({ active, done, label, num }) {
  return (
    <div className="flex items-center gap-2">
      <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold transition ${
        done ? 'bg-emerald-500 text-white'
        : active ? 'bg-orange-500 text-white ring-4 ring-orange-500/20'
        : 'bg-white/5 text-slate-500 border border-white/10'
      }`}>
        {done ? <CheckCircle2 className="w-4 h-4" /> : num}
      </div>
      <span className={`text-xs hidden md:inline ${active ? 'text-orange-300' : done ? 'text-emerald-400' : 'text-slate-500'}`}>{label}</span>
    </div>
  );
}

export default function MandateUploadPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const basePath = user?.role === 'admin' ? '/admin' : '/agent';
  const fileInput = useRef(null);
  const cameraInput = useRef(null);

  const [step, setStep] = useState(1); // 1=upload, 2=loading, 3=form, 4=invite, 5=done
  const [dragOver, setDragOver] = useState(false);
  const [filename, setFilename] = useState('');
  const [sourceType, setSourceType] = useState(null); // 'pdf' | 'image'
  const [uploadId, setUploadId] = useState(null);
  const [createdMandateId, setCreatedMandateId] = useState(null);
  const [createdMandateNumber, setCreatedMandateNumber] = useState(null);
  const [creating, setCreating] = useState(false);
  const [inviting, setInviting] = useState(false);
  const [conflict, setConflict] = useState(null);
  const [matchedDiscoveries, setMatchedDiscoveries] = useState([]);
  const [linkedDiscoveryId, setLinkedDiscoveryId] = useState(null);

  const [form, setForm] = useState({
    mandate_number: '',
    mandate_type: 'simple',
    signature_date: '',
    sellers: [{ first_name: '', last_name: '', email: '', phone: '' }],
    property_address: '',
    property_city: '',
    property_postal_code: '',
    property_type: 'apartment',
    surface_m2: '',
    rooms: '',
    sale_price: '',
    commission_percent: '',
    notes: '',
  });

  const [invite, setInvite] = useState({ email: '', phone: '', first_name: '', last_name: '' });

  // ── Upload (PDF ou photo) ──────────────────────────────────────────────────
  const handleFile = useCallback(async (file) => {
    if (!file) return;
    const name = (file.name || '').toLowerCase();
    const ext = '.' + name.split('.').pop();
    if (!ACCEPTED_EXTS.includes(ext)) {
      toast.error('Format non supporté (PDF, JPG, PNG, HEIC, WEBP uniquement)');
      return;
    }
    const isImage = ext !== '.pdf';
    const maxBytes = isImage ? 25 * 1024 * 1024 : 15 * 1024 * 1024;
    if (file.size > maxBytes) {
      toast.error(`Fichier trop volumineux (max ${maxBytes / 1024 / 1024} Mo)`);
      return;
    }
    setFilename(file.name);
    setStep(2);
    const fd = new FormData();
    fd.append('file', file);
    try {
      const res = await api.post('/mandate-upload/extract', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 120000,
      });
      const d = res.data.data || {};
      setUploadId(res.data.upload_id);
      setSourceType(res.data.source_type || (isImage ? 'image' : 'pdf'));
      setMatchedDiscoveries(res.data.matched_discoveries || []);
      // Pré-remplir le formulaire
      setForm(f => ({
        ...f,
        mandate_number: d.mandate_number || '',
        mandate_type: d.mandate_type || 'simple',
        signature_date: d.signature_date || '',
        sellers: (d.sellers && d.sellers.length > 0) ? d.sellers.map(s => ({
          first_name: s.first_name || '',
          last_name: s.last_name || '',
          email: s.email || '',
          phone: s.phone || '',
        })) : [{ first_name: '', last_name: '', email: '', phone: '' }],
        property_address: d.property_address || '',
        property_city: d.property_city || '',
        property_postal_code: d.property_postal_code || '',
        property_type: d.property_type || 'apartment',
        surface_m2: d.surface_m2 ?? '',
        rooms: d.rooms ?? '',
        sale_price: d.sale_price ?? '',
        commission_percent: d.commission_percent ?? '',
      }));
      // Pré-remplir l'invitation avec le 1er vendeur
      const first = d.sellers && d.sellers[0];
      if (first?.email) {
        setInvite({
          email: first.email || '',
          phone: first.phone || '',
          first_name: first.first_name || '',
          last_name: first.last_name || '',
        });
      }
      setStep(3);
      toast.success(isImage ? 'Photo analysée — vérifiez les données extraites' : 'Données extraites !');
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur d'analyse du document");
      setStep(1);
      setFilename('');
    }
  }, []);

  const onDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const f = e.dataTransfer.files?.[0];
    handleFile(f);
  };

  // ── Sellers ───────────────────────────────────────────────────────────────
  const addSeller = () => setForm(f => ({ ...f, sellers: [...f.sellers, { first_name: '', last_name: '', email: '', phone: '' }] }));
  const removeSeller = (idx) => setForm(f => ({ ...f, sellers: f.sellers.filter((_, i) => i !== idx) }));
  const updateSeller = (idx, field, val) => setForm(f => ({
    ...f, sellers: f.sellers.map((s, i) => i === idx ? { ...s, [field]: val } : s)
  }));

  // ── Création mandat ───────────────────────────────────────────────────────
  const createMandate = async () => {
    if (!form.sellers.some(s => s.first_name && s.last_name)) {
      toast.error('Au moins un vendeur (prénom + nom) requis');
      return;
    }
    if (!form.property_address || !form.property_city) {
      toast.error('Adresse du bien requise');
      return;
    }
    if (!form.sale_price) {
      toast.error('Prix de vente requis');
      return;
    }
    setCreating(true);
    setConflict(null);
    try {
      const payload = {
        upload_id: uploadId,
        data: {
          mandate_number: form.mandate_number || '',
          mandate_type: form.mandate_type,
          signature_date: form.signature_date || null,
          sellers: form.sellers.filter(s => s.first_name || s.last_name).map(s => ({
            first_name: s.first_name, last_name: s.last_name,
            email: s.email || null, phone: s.phone || null,
          })),
          property_address: form.property_address,
          property_city: form.property_city,
          property_postal_code: form.property_postal_code,
          property_type: form.property_type,
          surface_m2: form.surface_m2 ? Number(form.surface_m2) : null,
          rooms: form.rooms ? Number(form.rooms) : null,
          sale_price: Number(form.sale_price),
          commission_percent: form.commission_percent ? Number(form.commission_percent) : null,
        },
        notes: form.notes || null,
      };
      const res = await api.post('/mandate-upload/create-from-pdf', payload);
      setCreatedMandateId(res.data.mandate_id);
      setCreatedMandateNumber(res.data.mandate_number);
      // Refresh mandate list & related caches so /admin/mandates & sidebar
      // counter show the new mandate immediately without a hard reload.
      invalidateApiCache('/mandates-v2');
      invalidateApiCache('/mandate-upload');
      // Flag consumed by MandatesPage to display a discrete "Synchronisé"
      // badge on next arrival (reassures the user the list reflects the new
      // upload without needing to reload).
      try { sessionStorage.setItem('mandates_just_updated', String(Date.now())); } catch (e) {}
      toast.success(`Mandat n°${res.data.mandate_number} créé`);

      // AI cross-check : lier automatiquement la meilleure découverte si match haut score
      if (matchedDiscoveries.length > 0) {
        const best = matchedDiscoveries[0];
        // Auto-link si score >= 60 (très forte confiance)
        if (best.score >= 60) {
          try {
            await api.post('/mandate-upload/link-discovery', {
              mandate_id: res.data.mandate_id,
              discovery_id: best.id,
            });
            setLinkedDiscoveryId(best.id);
            toast.success(`Découverte "${best.prospect_name}" auto-liée et classifiée en "Mandat signé"`, { duration: 5000 });
          } catch (linkErr) {
            // silent — l'agent pourra lier manuellement depuis le détail mandat
          }
        }
      }

      // Préparer l'invitation
      const first = form.sellers[0] || {};
      if (first.email && !invite.email) {
        setInvite({
          email: first.email,
          phone: first.phone || '',
          first_name: first.first_name || '',
          last_name: first.last_name || '',
        });
      }
      setStep(4);
    } catch (e) {
      const status = e.response?.status;
      const detail = e.response?.data?.detail || 'Erreur création mandat';
      if (status === 409) {
        setConflict(detail);
        toast.error('Conflit d\'exclusivité détecté');
      } else {
        toast.error(detail);
      }
    } finally {
      setCreating(false);
    }
  };

  // ── Invitation vendeur ────────────────────────────────────────────────────
  const sendInvite = async () => {
    if (!invite.email) {
      toast.error('Email vendeur requis');
      return;
    }
    setInviting(true);
    try {
      const res = await api.post(`/mandate-upload/${createdMandateId}/invite-seller`, {
        email: invite.email,
        phone: invite.phone || null,
        first_name: invite.first_name || '',
        last_name: invite.last_name || '',
      });
      if (res.data.email_sent) {
        toast.success('Invitation envoyée au vendeur');
      } else {
        toast.warning("Mandat créé, mais l'envoi d'email a échoué. Lien partageable copié.");
        try { await navigator.clipboard.writeText(res.data.invite_link); } catch {}
      }
      setStep(5);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur envoi invitation');
    } finally {
      setInviting(false);
    }
  };

  const skipInvite = () => setStep(5);

  return (
    <div className="container mx-auto px-4 py-8 max-w-3xl" data-testid="mandate-upload-page">
      <Button variant="ghost" onClick={() => navigate(`${basePath}/mandates`)} className="mb-4" data-testid="back-btn">
        <ArrowLeft className="w-4 h-4 mr-2" />Retour aux mandats
      </Button>

      <motion.div initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }} className="mb-6">
        <h1 className="text-2xl sm:text-3xl font-bold flex items-center gap-2">
          <Sparkles className="w-7 h-7 text-orange-400" />
          Uploader un mandat
        </h1>
        <p className="text-muted-foreground text-sm mt-1">
          Importez le PDF ou la photo de votre mandat signé. L&apos;IA pré-remplit les champs en quelques secondes — vous validez avant enregistrement.
        </p>
      </motion.div>

      {/* Stepper */}
      <div className="flex items-center justify-between mb-8 px-2">
        <StepDot num={1} active={step === 1} done={step > 1} label="Upload" />
        <div className="flex-1 h-px bg-white/10 mx-2" />
        <StepDot num={2} active={step === 2} done={step > 2} label="Analyse IA" />
        <div className="flex-1 h-px bg-white/10 mx-2" />
        <StepDot num={3} active={step === 3} done={step > 3} label="Validation" />
        <div className="flex-1 h-px bg-white/10 mx-2" />
        <StepDot num={4} active={step === 4} done={step > 4} label="Vendeur" />
      </div>

      <AnimatePresence mode="wait">
        {/* Step 1: Upload (PDF ou photo) */}
        {step === 1 && (
          <motion.div key="step1" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="space-y-4">
            <div
              onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
              onDragLeave={() => setDragOver(false)}
              onDrop={onDrop}
              onClick={() => fileInput.current?.click()}
              className={`rounded-2xl border-2 border-dashed p-8 sm:p-12 text-center cursor-pointer transition ${
                dragOver ? 'border-orange-500 bg-orange-500/5' : 'border-white/15 hover:border-orange-500/40'
              }`}
              data-testid="upload-dropzone"
            >
              <Upload className="w-10 h-10 sm:w-12 sm:h-12 mx-auto text-orange-400 mb-3" />
              <div className="font-semibold mb-1">Glissez-déposez votre PDF ou photo</div>
              <div className="text-sm text-muted-foreground">ou cliquez pour parcourir · PDF jusqu'à 15 Mo, photos JPG/PNG/HEIC jusqu'à 25 Mo</div>
              <input
                ref={fileInput}
                type="file"
                accept={ACCEPTED_MIMES}
                className="hidden"
                onChange={(e) => handleFile(e.target.files?.[0])}
                data-testid="upload-input"
              />
            </div>

            {/* Bouton photo mobile-first */}
            <button
              type="button"
              onClick={() => cameraInput.current?.click()}
              className="w-full md:hidden flex items-center justify-center gap-2 rounded-2xl border-2 border-orange-500/40 bg-orange-500/10 px-4 py-4 font-bold text-orange-300 active:scale-95 transition"
              data-testid="camera-capture-btn"
            >
              <Camera className="w-5 h-5" />
              Prendre en photo le mandat
            </button>
            <input
              ref={cameraInput}
              type="file"
              accept="image/*"
              capture="environment"
              className="hidden"
              onChange={(e) => handleFile(e.target.files?.[0])}
              data-testid="camera-capture-input"
            />

            <div className="text-center text-xs text-muted-foreground">
              <Sparkles className="w-3 h-3 inline mr-1 text-orange-400" />
              L'IA extrait : numéro, vendeur, adresse, prix, surface, honoraires…
              <br />
              <span className="text-amber-300/80">Photo de mandat papier ? L&apos;IA peut faire des erreurs sur l&apos;écriture manuscrite — vous validez tout avant enregistrement.</span>
            </div>
          </motion.div>
        )}

        {/* Step 2: Loading */}
        {step === 2 && (
          <motion.div key="step2" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="rounded-2xl border p-12 text-center bg-card">
            <div className="relative w-20 h-20 mx-auto mb-4">
              <Loader2 className="w-20 h-20 absolute inset-0 text-orange-500 animate-spin" />
              <FileText className="w-8 h-8 absolute inset-0 m-auto text-orange-400" />
            </div>
            <div className="font-semibold mb-1">L'IA analyse votre mandat…</div>
            <div className="text-sm text-muted-foreground">{filename}</div>
            <div className="text-xs text-muted-foreground mt-3">Cela prend généralement 5 à 15 secondes.</div>
          </motion.div>
        )}

        {/* Step 3: Form */}
        {step === 3 && (
          <motion.div key="step3" initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
            className="space-y-5" data-testid="extracted-form">
            <div className="rounded-xl border bg-emerald-500/5 border-emerald-500/20 p-3 flex items-center gap-2 text-sm">
              <Sparkles className="w-4 h-4 text-emerald-400" />
              <span>
                Données extraites depuis <strong>{filename}</strong>{sourceType === 'image' ? ' (photo)' : ''}. Vérifiez et corrigez si besoin.
                {sourceType === 'image' && (
                  <span className="block text-amber-300/80 text-xs mt-1">
                    ⚠️ Photo de mandat — l'IA peut faire des erreurs sur l'écriture manuscrite. Vérifiez bien chaque champ.
                  </span>
                )}
              </span>
            </div>

            {/* AI Cross-check : découvertes correspondantes */}
            {matchedDiscoveries.length > 0 && !linkedDiscoveryId && (
              <motion.div
                initial={{ opacity: 0, y: -5 }}
                animate={{ opacity: 1, y: 0 }}
                className="rounded-xl border border-[#FF914D]/40 bg-gradient-to-br from-[#FF914D]/10 via-card to-[#681793]/10 p-4"
                data-testid="matched-discoveries"
              >
                <div className="flex items-center gap-2 mb-3">
                  <Link2 className="w-4 h-4 text-[#FF914D]" />
                  <p className="font-bold text-sm text-foreground">
                    Découverte{matchedDiscoveries.length > 1 ? 's' : ''} trouvée{matchedDiscoveries.length > 1 ? 's' : ''} qui correspond{matchedDiscoveries.length > 1 ? 'ent' : ''} à ce mandat
                  </p>
                </div>
                <p className="text-xs text-muted-foreground mb-3">
                  Liez ce mandat à une découverte pour auto-classifier cette opportunité en "Mandat signé" et éviter le double-comptage.
                </p>
                <div className="space-y-2">
                  {matchedDiscoveries.map((m) => (
                    <div
                      key={m.id}
                      className="flex items-center justify-between gap-2 p-3 rounded-lg bg-card/60 border border-white/10"
                      data-testid={`matched-discovery-${m.id}`}
                    >
                      <div className="min-w-0 flex-1">
                        <p className="font-medium text-sm text-foreground truncate">
                          {m.prospect_name || 'Prospect'}
                        </p>
                        <p className="text-xs text-muted-foreground truncate">
                          {m.address || '—'}{m.city ? `, ${m.city}` : ''}
                        </p>
                        <div className="flex items-center gap-2 mt-1 flex-wrap">
                          <Badge variant="outline" className="text-[10px] border-emerald-500/40 text-emerald-300">
                            Score {m.score}
                          </Badge>
                          {(m.reasons || []).map((r) => (
                            <Badge key={r} variant="outline" className="text-[10px] border-white/20 text-muted-foreground">
                              {r}
                            </Badge>
                          ))}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
                <p className="text-[10px] text-muted-foreground mt-3 italic">
                  Le lien sera créé après enregistrement du mandat — vous pourrez choisir la découverte à associer.
                </p>
              </motion.div>
            )}

            {linkedDiscoveryId && (
              <div className="rounded-xl border border-emerald-500/30 bg-emerald-500/5 p-3 flex items-center gap-2 text-sm" data-testid="linked-discovery-confirmation">
                <CheckCircle2 className="w-4 h-4 text-emerald-400" />
                <span>Découverte liée — auto-classifiée en "Mandat signé"</span>
              </div>
            )}

            {conflict && (
              <motion.div initial={{ opacity: 0, y: -5 }} animate={{ opacity: 1, y: 0 }}
                className="rounded-xl border border-red-500/40 bg-red-500/10 p-4 text-sm" data-testid="conflict-warning">
                <div className="font-semibold text-red-300 mb-1 flex items-center gap-2">
                  <FileText className="w-4 h-4" />Conflit d'exclusivité
                </div>
                <p className="text-red-200/90">{conflict}</p>
                <p className="text-red-300/70 text-xs mt-2">Astuce : passez le type de mandat en "Simple" pour contourner l'exclusivité.</p>
              </motion.div>
            )}

            {/* Mandat */}
            <div className="rounded-xl border bg-card p-4">
              <div className="text-sm font-semibold mb-3 flex items-center gap-2">
                <FileText className="w-4 h-4 text-orange-400" />Mandat
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <div>
                  <Label>N° mandat *</Label>
                  <Input value={form.mandate_number} onChange={(e) => setForm({ ...form, mandate_number: e.target.value })} data-testid="field-mandate-number" />
                </div>
                <div>
                  <Label>Type de mandat</Label>
                  <Select value={form.mandate_type} onValueChange={(v) => setForm({ ...form, mandate_type: v })}>
                    <SelectTrigger data-testid="field-mandate-type"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {MANDATE_TYPES.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Date signature</Label>
                  <Input type="date" value={form.signature_date || ''} onChange={(e) => setForm({ ...form, signature_date: e.target.value })} data-testid="field-signature-date" />
                </div>
              </div>
            </div>

            {/* Vendeurs */}
            <div className="rounded-xl border bg-card p-4">
              <div className="flex items-center justify-between mb-3">
                <div className="text-sm font-semibold flex items-center gap-2"><UserIcon className="w-4 h-4 text-orange-400" />Vendeur(s)</div>
                <Button variant="outline" size="sm" onClick={addSeller} data-testid="add-seller-btn">
                  <Plus className="w-3 h-3 mr-1" />Ajouter
                </Button>
              </div>
              <div className="space-y-3">
                {form.sellers.map((s, idx) => (
                  <div key={idx} className="grid grid-cols-1 md:grid-cols-5 gap-2 items-end" data-testid={`seller-row-${idx}`}>
                    <div className="md:col-span-1">
                      <Label className="text-xs">Prénom *</Label>
                      <Input value={s.first_name} onChange={(e) => updateSeller(idx, 'first_name', e.target.value)} />
                    </div>
                    <div className="md:col-span-1">
                      <Label className="text-xs">Nom *</Label>
                      <Input value={s.last_name} onChange={(e) => updateSeller(idx, 'last_name', e.target.value)} />
                    </div>
                    <div className="md:col-span-2">
                      <Label className="text-xs">Email</Label>
                      <Input type="email" value={s.email} onChange={(e) => updateSeller(idx, 'email', e.target.value)} />
                    </div>
                    <div className="md:col-span-1 flex gap-1">
                      <div className="flex-1">
                        <Label className="text-xs">Téléphone</Label>
                        <Input value={s.phone} onChange={(e) => updateSeller(idx, 'phone', e.target.value)} />
                      </div>
                      {form.sellers.length > 1 && (
                        <Button variant="ghost" size="icon" onClick={() => removeSeller(idx)} className="text-red-400 mt-5" data-testid={`remove-seller-${idx}`}>
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Bien */}
            <div className="rounded-xl border bg-card p-4">
              <div className="text-sm font-semibold mb-3 flex items-center gap-2">
                <Home className="w-4 h-4 text-orange-400" />Bien immobilier
              </div>
              <div className="grid grid-cols-1 md:grid-cols-6 gap-3">
                <div className="md:col-span-4">
                  <Label>Adresse *</Label>
                  <Input value={form.property_address} onChange={(e) => setForm({ ...form, property_address: e.target.value })} data-testid="field-address" />
                </div>
                <div className="md:col-span-1">
                  <Label>CP</Label>
                  <Input value={form.property_postal_code} onChange={(e) => setForm({ ...form, property_postal_code: e.target.value })} />
                </div>
                <div className="md:col-span-1">
                  <Label>Ville *</Label>
                  <Input value={form.property_city} onChange={(e) => setForm({ ...form, property_city: e.target.value })} data-testid="field-city" />
                </div>
                <div className="md:col-span-2">
                  <Label>Type</Label>
                  <Select value={form.property_type} onValueChange={(v) => setForm({ ...form, property_type: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {PROPERTY_TYPES.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="md:col-span-2">
                  <Label>Surface (m²)</Label>
                  <Input type="number" value={form.surface_m2} onChange={(e) => setForm({ ...form, surface_m2: e.target.value })} />
                </div>
                <div className="md:col-span-2">
                  <Label>Pièces</Label>
                  <Input type="number" value={form.rooms} onChange={(e) => setForm({ ...form, rooms: e.target.value })} />
                </div>
              </div>
            </div>

            {/* Prix */}
            <div className="rounded-xl border bg-card p-4">
              <div className="text-sm font-semibold mb-3 flex items-center gap-2">
                <Euro className="w-4 h-4 text-orange-400" />Tarification
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <Label>Prix de vente (€) *</Label>
                  <Input type="number" value={form.sale_price} onChange={(e) => setForm({ ...form, sale_price: e.target.value })} data-testid="field-sale-price" />
                </div>
                <div>
                  <Label>Honoraires (%)</Label>
                  <Input type="number" step="0.01" value={form.commission_percent} onChange={(e) => setForm({ ...form, commission_percent: e.target.value })} />
                </div>
              </div>
            </div>

            <div className="rounded-xl border bg-card p-4">
              <Label>Notes internes</Label>
              <Textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
            </div>

            <div className="flex justify-between gap-2">
              <Button variant="outline" onClick={() => { setStep(1); setFilename(''); setUploadId(null); }}>
                Recommencer
              </Button>
              <Button onClick={createMandate} disabled={creating} className="bg-orange-500 hover:bg-orange-600" data-testid="create-mandate-btn">
                {creating && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                Enregistrer le mandat
              </Button>
            </div>
          </motion.div>
        )}

        {/* Step 4: Invite seller */}
        {step === 4 && (
          <motion.div key="step4" initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
            data-testid="invite-step">
            <div className="rounded-xl border bg-emerald-500/5 border-emerald-500/30 p-4 mb-5 flex items-center gap-3">
              <CheckCircle2 className="w-5 h-5 text-emerald-400" />
              <div className="text-sm">
                <strong>Mandat n°{createdMandateNumber} créé.</strong> Souhaitez-vous inviter le vendeur sur son espace Homly ?
              </div>
            </div>

            <div className="rounded-xl border bg-card p-5 space-y-4">
              <div className="text-sm">
                <p className="font-semibold mb-1">Pourquoi inviter le vendeur ?</p>
                <ul className="text-muted-foreground space-y-1 ml-4 list-disc text-xs">
                  <li>Il pourra <strong>noter votre travail</strong> et booster votre HomlyScore</li>
                  <li>Il verra en temps réel les <strong>visites, agences et offres</strong></li>
                  <li>C'est un argument commercial fort de transparence</li>
                </ul>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <Label>Prénom</Label>
                  <Input value={invite.first_name} onChange={(e) => setInvite({ ...invite, first_name: e.target.value })} />
                </div>
                <div>
                  <Label>Nom</Label>
                  <Input value={invite.last_name} onChange={(e) => setInvite({ ...invite, last_name: e.target.value })} />
                </div>
                <div>
                  <Label>Email *</Label>
                  <Input type="email" value={invite.email} onChange={(e) => setInvite({ ...invite, email: e.target.value })} placeholder="vendeur@example.com" data-testid="invite-email" />
                </div>
                <div>
                  <Label>Téléphone</Label>
                  <Input value={invite.phone} onChange={(e) => setInvite({ ...invite, phone: e.target.value })} />
                </div>
              </div>

              <div className="flex flex-col-reverse md:flex-row gap-2 justify-end pt-2">
                <Button variant="outline" onClick={skipInvite} data-testid="skip-invite-btn">Plus tard</Button>
                <Button onClick={sendInvite} disabled={inviting} className="bg-orange-500 hover:bg-orange-600" data-testid="send-invite-btn">
                  {inviting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
                  Envoyer l'invitation
                </Button>
              </div>
            </div>
          </motion.div>
        )}

        {/* Step 5: Done */}
        {step === 5 && (
          <motion.div key="step5" initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0 }}
            className="rounded-2xl border p-10 text-center bg-gradient-to-br from-emerald-500/10 to-card">
            <CheckCircle2 className="w-16 h-16 mx-auto text-emerald-400 mb-3" />
            <h2 className="text-2xl font-bold mb-1">Tout est prêt !</h2>
            <p className="text-muted-foreground text-sm mb-5">Mandat n°{createdMandateNumber} enregistré.</p>
            <div className="flex flex-col md:flex-row gap-2 justify-center">
              <Button variant="outline" onClick={() => { window.location.reload(); }}>
                <Plus className="w-4 h-4 mr-2" />Uploader un autre mandat
              </Button>
              <Button onClick={() => navigate(`${basePath}/mandates/v2/${createdMandateId}`)} className="bg-orange-500 hover:bg-orange-600" data-testid="goto-mandate-btn">
                Voir la fiche du mandat
              </Button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
